import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Clinic, CreateClinic } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { clinicService } from '@/services/clinic_service'
import { canConfirmClinicDeletion, type ClinicDeletionImpact } from './clinicDeletion'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { useViewMode } from '@/stores/viewMode'
import { Uploader } from '@/components/Uploader'
import { Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'
import { TagInput } from '@/components/ui/tag-input'
import { Badge } from '@/components/ui/badge'
import { useProviderTags } from '@/hooks/useProviderTags'

// 標籤編輯只開放 admin / provider clinic 管理者，不含 clinic_group / clinic / supplier
const CLINIC_TAGS_EDIT_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.U },
  { module: 'provider_clinic_management', action: AllowedAction.U },
]

// admin/provider/clinic_group 有 clinic_management U 者可編輯 ops-level 欄位
// （診所 IP、天氣金鑰、醫事機構代碼、位置資訊、是否啟用…）
const CLINIC_ADVANCED_EDIT_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.U },
  { module: 'provider_clinic_management', action: AllowedAction.U },
  { module: 'clinic_group_clinic_management', action: AllowedAction.U },
]

// 刪除診所（同三 scope 的 D）
const CLINIC_DELETE_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.D },
  { module: 'provider_clinic_management', action: AllowedAction.D },
  { module: 'clinic_group_clinic_management', action: AllowedAction.D },
]
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSecondOrderDivisions } from '@/hooks/useSecondOrderDivisions'
import { useChildrenDivisions } from '@/hooks/useChildrenDivisions'
import { ClinicAppointmentModeCard } from './ClinicAppointmentModeCard'

export const clinicFormSchema = z.object({
  clinic_name: z.string().min(1, '請輸入診所名稱'),
  alias: z.string().optional(), // 診所別名（選填）
  clinic_ip: z.string().min(1, '請輸入診所IP'),
  manager_name: z.string().min(1, '請輸入聯絡人姓名'),
  phone: z.string().min(1, '請輸入電話'),
  business_hour: z.string().min(1, '請輸入營業時間'),
  address: z.string().min(1, '請輸入地址'),
  medical_institution_code: z.string().max(20, '醫事機構代碼不能超過20個字元').optional(),
  tags: z.array(z.string().min(1).max(50)).default([]),
  area: z.string().optional(), // 地區設為選填
  lat: z.string().optional(),
  lng: z.string().optional(),
  weather_key: z.string().min(1, '請輸入天氣金鑰'),
  is_active: z.boolean().default(true),
  show_patient_name: z.boolean().default(false), // 叫號時是否顯示病患姓名
  show_doctor_name: z.boolean().default(false), // 叫號時是否顯示醫師名稱
  logo_file_uuid: z.string().nullable().optional(),
})

type ClinicFormValues = z.infer<typeof clinicFormSchema>

interface ClinicInfoProps {
  clinic?: Clinic
  isLoading?: boolean
  mode: 'create' | 'edit'
}

export default function ClinicInfo({ clinic, isLoading, mode }: ClinicInfoProps) {
  const { token } = useAuth()
  const navigate = useNavigate()
  const { mode: viewMode, id: viewModeId } = useViewMode()
  const [logoFileUuid, setLogoFileUuid] = useState<string | null>(clinic?.logo_file_uuid || null)
  const [showDeleteLogoDialog, setShowDeleteLogoDialog] = useState(false)
  const [showDeleteClinicDialog, setShowDeleteClinicDialog] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [deletionImpact, setDeletionImpact] = useState<ClinicDeletionImpact | null>(null)
  const [loadingImpact, setLoadingImpact] = useState(false)
  const [selectedSecondOrderId, setSelectedSecondOrderId] = useState<number | null>(null)

  // 取得台灣的二級行政區（縣市）
  const { divisions: secondOrderDivisions, isLoading: isLoadingSecondOrder } =
    useSecondOrderDivisions('TW', 100)

  // 取得選定二級行政區的三級行政區（鄉鎮市區）
  const { children: thirdOrderDivisions, isLoading: isLoadingThirdOrder } =
    useChildrenDivisions(selectedSecondOrderId)

  // 取得 Provider 旗下既有標籤 (autocomplete catalog)
  const { tags: providerTags } = useProviderTags({
    providerId: clinic?.provider_id,
    type: 'clinic',
  })

  const form = useForm<ClinicFormValues>({
    // resolver: zodResolver(clinicFormSchema),
    defaultValues: {
      clinic_name: clinic?.clinic_name ?? '',
      alias: clinic?.alias ?? '',
      clinic_ip: clinic?.clinic_ip ?? '',
      manager_name: clinic?.manager_name ?? '',
      phone: clinic?.phone ?? '',
      business_hour: clinic?.business_hour ?? '',
      address: clinic?.address ?? '',
      medical_institution_code: clinic?.medical_institution_code ?? '',
      tags: clinic?.tags ?? [],
      area: clinic?.area ?? '',
      lat: clinic?.lat ?? '',
      lng: clinic?.lng ?? '',
      weather_key: clinic?.weather_key ?? '',
      is_active: clinic?.is_active ?? true,
      show_patient_name: clinic?.show_patient_name ?? false,
      show_doctor_name: clinic?.show_doctor_name ?? false,
      logo_file_uuid: clinic?.logo_file_uuid || null,
    },
  })

  useEffect(() => {
    if (clinic && mode === 'edit') {
      form.reset({
        clinic_name: clinic.clinic_name,
        alias: clinic.alias ?? '',
        clinic_ip: clinic.clinic_ip,
        manager_name: clinic.manager_name,
        phone: clinic.phone,
        business_hour: clinic.business_hour,
        address: clinic.address,
        medical_institution_code: clinic.medical_institution_code ?? '',
        tags: clinic.tags ?? [],
        area: clinic.area,
        lat: clinic.lat ?? '',
        lng: clinic.lng ?? '',
        weather_key: clinic.weather_key,
        is_active: clinic.is_active,
        show_patient_name: clinic.show_patient_name ?? false,
        show_doctor_name: clinic.show_doctor_name ?? false,
        logo_file_uuid: clinic.logo_file_uuid,
      })
      setLogoFileUuid(clinic.logo_file_uuid)
    }
  }, [clinic, form, mode])

  const onSubmit = async (data: ClinicFormValues) => {
    if (!token) return

    try {
      // Prepare data with optional fields properly handled
      // Filter out empty lat and lng values, but ensure area has a default value
      const processedData = {
        ...data,
        logo_file_uuid: logoFileUuid,
        lat: data.lat && data.lat.trim() !== '' ? data.lat : undefined,
        lng: data.lng && data.lng.trim() !== '' ? data.lng : undefined,
        // Area is required in the database schema, so provide a default value if empty
        area: data.area && data.area.trim() !== '' ? data.area : '未指定地區',
      }

      let result

      if (mode === 'create') {
        // Create new clinic
        const createData: CreateClinic = {
          clinic_name: processedData.clinic_name,
          clinic_ip: processedData.clinic_ip,
          manager_name: processedData.manager_name,
          phone: processedData.phone,
          business_hour: processedData.business_hour,
          address: processedData.address,
          medical_institution_code: processedData.medical_institution_code || undefined,
          area: processedData.area,
          weather_key: processedData.weather_key,
          is_active: processedData.is_active,
          logo_file_uuid: processedData.logo_file_uuid,
          // sys_admin 在 provider mode 時需要帶入 provider_id
          // provider_admin 可由 backend 自動取得
          ...(viewMode === 'provider' && viewModeId ? { provider_id: viewModeId } : {}),
        }

        // Only add lat and lng if they are defined
        if (processedData.lat) createData.lat = processedData.lat
        if (processedData.lng) createData.lng = processedData.lng

        result = await clinicService.createClinic(createData, token)
      } else {
        // Update existing clinic
        result = await clinicService.updateClinic(clinic!.clinic_id, processedData, token)
      }

      if (result.success) {
        toast.success(`診所 ${data.clinic_name} 已${mode === 'create' ? '建立' : '更新'}`)
        if (mode === 'create') {
          navigate(-1)
        }
      } else {
        toast.error(result.error?.message || `診所${mode === 'create' ? '建立' : '更新'}失敗`)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  // 打開刪除對話框前先取得「連鎖刪除影響」，讓使用者看到會被刪掉哪些資料/帳號。
  const openDeleteClinicDialog = async () => {
    if (!token || !clinic) return
    setDeleteConfirmInput('')
    setDeletionImpact(null)
    setLoadingImpact(true)
    setShowDeleteClinicDialog(true)
    try {
      const res = await clinicService.getClinicDeletionImpact(clinic.clinic_id, token)
      if (res.success && res.data) {
        setDeletionImpact(res.data)
      } else {
        toast.error(res.error?.message || '取得刪除影響失敗')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    } finally {
      setLoadingImpact(false)
    }
  }

  const handleDeleteClinic = async () => {
    if (!token || !clinic) return

    // Close the dialog first
    setShowDeleteClinicDialog(false)
    setDeleteConfirmInput('')

    try {
      const result = await clinicService.deleteClinic(clinic.clinic_id, token)

      if (result.success) {
        toast.success(`診所 ${clinic.clinic_name} 已刪除`)
        navigate(-1)
      } else {
        toast.error(result.error?.message || '診所刪除失敗')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  if (mode === 'edit' && isLoading) {
    return <div>載入中...</div>
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{mode === 'create' ? '新增診所' : '編輯診所'}</CardTitle>
          <CardDescription>
            {mode === 'create' ? '建立新的診所資訊' : '編輯現有診所資訊'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <div className="space-y-8">
              <FormField
                control={form.control}
                name="clinic_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>診所名稱</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入診所名稱" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <FormField
                  control={form.control}
                  name="alias"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>診所別名（選填）</FormLabel>
                      <FormControl>
                        <Input placeholder="請輸入診所別名" {...field} />
                      </FormControl>
                      <FormDescription>
                        用於後台數據顯示，當診所 Logo 已包含名稱時可使用別名識別
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <FormItem>
                <FormLabel>診所 Logo</FormLabel>
                <FormControl>
                  <div className="mt-2">
                    {logoFileUuid ? (
                      <div className="relative group mb-4 inline-block">
                        <img
                          src={`${import.meta.env.VITE_API_URL}/api/files/download/${logoFileUuid}`}
                          alt="診所 Logo"
                          className="h-32 object-cover rounded-md"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          type="button"
                          className="absolute right-2 top-2 hidden group-hover:flex"
                          onClick={() => setShowDeleteLogoDialog(true)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Uploader
                        folder="clinic_logos"
                        allowedFileTypes={['.jpg', '.jpeg', '.png', '.webp']}
                        onUploadSuccess={({ uuid }) => {
                          setLogoFileUuid(uuid)
                          form.setValue('logo_file_uuid', uuid)
                        }}
                      />
                    )}
                  </div>
                </FormControl>
                <FormDescription>
                  上傳診所 Logo 圖片（支援 jpg、jpeg、png、webp 格式）
                </FormDescription>
                <FormMessage />
              </FormItem>

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <FormField
                  control={form.control}
                  name="clinic_ip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>診所IP</FormLabel>
                      <FormControl>
                        <Input placeholder="請輸入診所IP" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <FormField
                control={form.control}
                name="manager_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>聯絡人姓名</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入聯絡人姓名" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電話</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電話" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_hour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>營業時間</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入營業時間" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>地址</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入地址" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <FormField
                  control={form.control}
                  name="medical_institution_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>醫事機構代碼（選填）</FormLabel>
                      <FormControl>
                        <Input placeholder="請輸入醫事機構代碼" maxLength={20} {...field} />
                      </FormControl>
                      <FormDescription>醫事機構代碼，最多20個字元</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <IfAnyPermission
                permissions={CLINIC_TAGS_EDIT_PERMISSIONS}
                fallback={
                  clinic?.tags && clinic.tags.length > 0 ? (
                    <FormItem>
                      <FormLabel>標籤</FormLabel>
                      <div className="flex flex-wrap gap-1">
                        {clinic.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </FormItem>
                  ) : null
                }
              >
                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>標籤（選填）</FormLabel>
                      <FormControl>
                        <TagInput
                          value={field.value ?? []}
                          onChange={field.onChange}
                          availableTags={providerTags}
                          disabled={!clinic?.provider_id}
                        />
                      </FormControl>
                      <FormDescription>
                        同 Provider 旗下診所共用標籤，輸入新標籤後存檔即建立
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <Card className="border-dashed p-0 mb-6">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">位置資訊</CardTitle>
                    <CardDescription>
                      選擇縣市與鄉鎮市區後，系統將自動填入經緯度資訊（選填）
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* 縣市選擇 */}
                      <div>
                        <FormLabel className="text-sm font-medium">縣市（選填）</FormLabel>
                        <Select
                          disabled={isLoadingSecondOrder}
                          onValueChange={(value) => {
                            const geonameId = parseInt(value, 10)
                            setSelectedSecondOrderId(geonameId)
                            // 清空鄉鎮市區、經緯度
                            form.setValue('area', '')
                            form.setValue('lat', '')
                            form.setValue('lng', '')
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="請選擇縣市" />
                          </SelectTrigger>
                          <SelectContent>
                            {secondOrderDivisions?.map((division) => (
                              <SelectItem
                                key={division.geonameId}
                                value={division.geonameId.toString()}
                              >
                                {division.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* 鄉鎮市區選擇 */}
                      <div>
                        <FormLabel className="text-sm font-medium">鄉鎮市區（選填）</FormLabel>
                        <FormField
                          control={form.control}
                          name="area"
                          render={({ field }) => (
                            <Select
                              disabled={isLoadingThirdOrder || !selectedSecondOrderId}
                              onValueChange={(value) => {
                                // 找到選中的地區資料
                                const selectedArea = thirdOrderDivisions?.find(
                                  (area) => area.name === value,
                                )

                                // 更新表單中的地區名稱、經緯度
                                if (selectedArea) {
                                  field.onChange(selectedArea.name)
                                  form.setValue('lat', selectedArea.lat)
                                  form.setValue('lng', selectedArea.lng)
                                }
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="請選擇鄉鎮市區" />
                              </SelectTrigger>
                              <SelectContent>
                                {thirdOrderDivisions?.map((area) => (
                                  <SelectItem key={area.geonameId} value={area.name}>
                                    {area.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FormMessage />
                      </div>
                    </div>

                    {/* 經緯度資訊 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="lat"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>緯度 (Latitude)（選填）</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="自動填入緯度或手動輸入"
                                {...field}
                                className={field.value ? 'bg-muted' : ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lng"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>經度 (Longitude)（選填）</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="自動填入經度或手動輸入"
                                {...field}
                                className={field.value ? 'bg-muted' : ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </IfAnyPermission>

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <FormField
                  control={form.control}
                  name="weather_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>天氣金鑰</FormLabel>
                      <FormControl>
                        <Input placeholder="請輸入天氣金鑰" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>啟用狀態</FormLabel>
                        <FormDescription>設定診所是否啟用</FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </IfAnyPermission>

              <FormField
                control={form.control}
                name="show_patient_name"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>叫號顯示病患姓名</FormLabel>
                      <FormDescription>設定叫號時是否顯示病患姓名</FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="show_doctor_name"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>叫號顯示醫師名</FormLabel>
                      <FormDescription>設定叫號時是否顯示醫師名稱</FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <div className="flex justify-between w-full">
                <Button type="button" onClick={form.handleSubmit(onSubmit)}>
                  {mode === 'create' ? '建立診所' : '更新診所'}
                </Button>

                {mode === 'edit' && (
                  <IfAnyPermission permissions={CLINIC_DELETE_PERMISSIONS}>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={openDeleteClinicDialog}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      刪除診所
                    </Button>
                  </IfAnyPermission>
                )}
              </div>
            </div>
          </Form>
        </CardContent>
      </Card>

      {/* 診所層級的預約模式切換（僅編輯模式 + 有 clinic_management U 者可見） */}
      {mode === 'edit' && clinic?.clinic_id && (
        <IfAnyPermission permissions={CLINIC_ADVANCED_EDIT_PERMISSIONS}>
          <ClinicAppointmentModeCard clinicId={clinic.clinic_id} />
        </IfAnyPermission>
      )}

      {/* 刪除 Logo 確認對話框 */}
      <AlertDialog open={showDeleteLogoDialog} onOpenChange={setShowDeleteLogoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除診所 Logo 嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原，刪除後需要重新上傳 Logo。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setLogoFileUuid(null)
                form.setValue('logo_file_uuid', null)
                setShowDeleteLogoDialog(false)
              }}
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 刪除診所確認對話框（連鎖刪除，含影響預覽） */}
      <AlertDialog open={showDeleteClinicDialog} onOpenChange={setShowDeleteClinicDialog}>
        <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除診所嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原。刪除後，以下 clinic 層級的資料將一併移除。
            </AlertDialogDescription>
          </AlertDialogHeader>

          {loadingImpact ? (
            <div className="py-4 text-sm text-muted-foreground">載入中…</div>
          ) : deletionImpact ? (
            <div className="space-y-3 py-2 text-sm">
              <div>
                <div className="font-medium">會一併刪除的資料：</div>
                <ul className="list-disc pl-5">
                  <li>使用者關聯 {deletionImpact.relatedCounts.clinicUsers} 筆</li>
                  <li>角色 {deletionImpact.relatedCounts.roles} 筆</li>
                  <li>醫師 {deletionImpact.relatedCounts.doctors} 筆</li>
                  <li>診間 {deletionImpact.relatedCounts.examinationRooms} 筆</li>
                  <li>叫號機 {deletionImpact.relatedCounts.queueMachines} 筆</li>
                  <li>預約 {deletionImpact.relatedCounts.appointments} 筆</li>
                  <li>模組設定 {deletionImpact.relatedCounts.clinicModules} 筆</li>
                  {deletionImpact.relatedCounts.broadcastDevicesUnlinked > 0 && (
                    <li>
                      廣播設備解除連結 {deletionImpact.relatedCounts.broadcastDevicesUnlinked} 台
                    </li>
                  )}
                </ul>
              </div>

              {deletionImpact.accountsToDelete.length > 0 && (
                <div className="text-destructive">
                  <div className="font-medium">以下帳號會被永久刪除：</div>
                  <ul className="list-disc pl-5">
                    {deletionImpact.accountsToDelete.map((a) => (
                      <li key={a.user_id}>
                        {a.user_name}（{a.account}）
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {deletionImpact.blockedAccounts.length > 0 && (
                <div className="rounded-md bg-yellow-50 p-3 text-yellow-800">
                  <div className="font-medium">
                    無法刪除：以下帳號另有其他存取，請先移除後再刪除診所。
                  </div>
                  <ul className="list-disc pl-5">
                    {deletionImpact.blockedAccounts.map((a) => (
                      <li key={a.user_id}>
                        {a.user_name}（{a.account}）
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {deletionImpact.canDelete && (
                <div>
                  <div className="mb-1">
                    請輸入診所名稱「{deletionImpact.clinic.clinic_name}」以確認刪除：
                  </div>
                  <Input
                    placeholder="請輸入診所名稱以確認刪除"
                    value={deleteConfirmInput}
                    onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  />
                </div>
              )}
            </div>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmInput('')}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClinic}
              disabled={!canConfirmClinicDeletion(deletionImpact, deleteConfirmInput)}
              className={
                !canConfirmClinicDeletion(deletionImpact, deleteConfirmInput)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
